import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { payrollApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState, Modal, ConfirmModal } from '../../components/shared/index';
import {
  PlusIcon, TrashIcon, ArrowTopRightOnSquareIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  draft:       'bg-gray-100  text-gray-700  border-gray-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  approved:    'bg-blue-100  text-blue-800  border-blue-200',
  exported:    'bg-green-100 text-green-800 border-green-200',
};

function StatusBadge({ status }) {
  return (
    <span className={clsx(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize',
      STATUS_STYLES[status] || STATUS_STYLES.draft,
    )}>
      {String(status || 'draft').replace('_', ' ')}
    </span>
  );
}

function NewPeriodModal({ open, onClose, onSubmit, pending }) {
  const [form, setForm] = useState({
    name: '', period_start: '', period_end: '', pay_date: '', currency: 'USD',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name && form.period_start && form.period_end;

  const reset = () => setForm({ name: '', period_start: '', period_end: '', pay_date: '', currency: 'USD' });

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="New payroll period" size="md">
      <div className="space-y-4">
        <div>
          <label className="label">Period name</label>
          <input
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. March 2026"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Start date</label>
            <input type="date" className="input" value={form.period_start} onChange={(e) => set('period_start', e.target.value)} />
          </div>
          <div>
            <label className="label">End date</label>
            <input type="date" className="input" value={form.period_end} onChange={(e) => set('period_end', e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Pay date (optional)</label>
            <input type="date" className="input" value={form.pay_date} onChange={(e) => set('pay_date', e.target.value)} />
          </div>
          <div>
            <label className="label">Currency</label>
            <select className="input" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              {['USD','EUR','GBP','INR','CAD','AUD','SGD','JPY'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => { reset(); onClose(); }} className="btn-secondary">Cancel</button>
          <button
            disabled={!valid || pending}
            onClick={() => onSubmit({
              name: form.name,
              period_start: form.period_start,
              period_end: form.period_end,
              pay_date: form.pay_date || null,
              currency: form.currency,
            })}
            className="btn-primary disabled:opacity-40"
          >
            {pending ? 'Creating…' : 'Create period'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function Payroll() {
  const qc = useQueryClient();
  const [openNew, setOpenNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payroll', 'periods'],
    queryFn:  () => payrollApi.periods().then((r) => r.data),
  });

  const createPeriod = useMutation({
    mutationFn: payrollApi.createPeriod,
    onSuccess:  () => {
      toast.success('Period created');
      setOpenNew(false);
      qc.invalidateQueries({ queryKey: ['payroll', 'periods'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to create period'),
  });

  const deletePeriod = useMutation({
    mutationFn: payrollApi.deletePeriod,
    onSuccess:  () => {
      toast.success('Period deleted');
      qc.invalidateQueries({ queryKey: ['payroll', 'periods'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to delete period'),
  });

  if (isLoading) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  const rows = data?.data || [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Payroll"
        subtitle="Prepare pay periods, generate employee runs, and export to your provider."
        actions={
          <button onClick={() => setOpenNew(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" /> New period
          </button>
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          title="No payroll periods yet"
          description="Create your first period to start generating runs from active employees and merit cycles."
          action={
            <button onClick={() => setOpenNew(true)} className="btn-primary">
              <PlusIcon className="w-4 h-4" /> New period
            </button>
          }
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['Period', 'Dates', 'Pay date', 'Runs', 'Currency', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.map((p) => (
                <tr key={p.id} className="tr-hover">
                  <td className="td font-medium text-gray-900">{p.name}</td>
                  <td className="td text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDaysIcon className="w-3.5 h-3.5 text-gray-400" />
                      {new Date(p.period_start).toLocaleDateString()} → {new Date(p.period_end).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="td text-sm text-gray-600">
                    {p.pay_date ? new Date(p.pay_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="td text-sm">{p.runs_count ?? 0}</td>
                  <td className="td text-sm">{p.currency || '—'}</td>
                  <td className="td"><StatusBadge status={p.status} /></td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/app/payroll/${p.id}`}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        title="Open"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </Link>
                      {p.status !== 'exported' && (
                        <button
                          onClick={() => setConfirmDelete(p)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewPeriodModal
        open={openNew}
        onClose={() => setOpenNew(false)}
        onSubmit={(d) => createPeriod.mutate(d)}
        pending={createPeriod.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deletePeriod.mutate(confirmDelete.id)}
        title="Delete payroll period?"
        message={`"${confirmDelete?.name}" and all its generated runs will be permanently removed.`}
        danger
      />
    </div>
  );
}
